/**
 * Accounts list page.
 *
 * Real table layout (Name / Visibility / Owner / Created), debounced
 * search across name, visibility chip filter (public / private), owner
 * dropdown for manager+, "no owner" toggle. Server-driven pagination.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Building2, Globe, Lock, Plus, Search, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { type Column, DataTable } from "../components/DataTable";
import { ImportDialog } from "../components/ImportDialog";
import { SearchableUserPicker } from "../components/SearchableUserPicker";
import { TagFilterChips } from "../components/tags/TagFilterChips";
import { type Account, accountsApi, usersApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";

export const Route = createFileRoute("/_app/accounts/")({
  component: AccountsListPage,
});

const PAGE_SIZE = 25;
type Visibility = "" | "public" | "private";

function AccountsListPage() {
  const user = useAuthStore((s) => s.user);
  const isManagerPlus = user?.role === "admin" || user?.role === "manager";
  const queryClient = useQueryClient();

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [ownerId, setOwnerId] = useState<string>("");
  const [visibility, setVisibility] = useState<Visibility>("");
  const [unowned, setUnowned] = useState(false);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const filters = {
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    q: debouncedQ.trim() || undefined,
    owner_id: ownerId || undefined,
    visibility: (visibility || undefined) as "public" | "private" | undefined,
    unowned: unowned || undefined,
    tag_ids: tagIds.length > 0 ? tagIds : undefined,
  };

  const listQuery = useQuery({
    queryKey: ["accounts", filters],
    queryFn: () => accountsApi.list(filters),
  });

  const usersQuery = useQuery({
    queryKey: ["users", "active"],
    queryFn: () => usersApi.list({ active_only: true }),
    enabled: isManagerPlus,
  });
  const usersById = useMemo(() => {
    const m = new Map<string, { id: string; email: string; name: string | null }>();
    for (const u of usersQuery.data?.items ?? []) m.set(u.id, u);
    return m;
  }, [usersQuery.data]);

  const data = listQuery.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const hasFilter = Boolean(debouncedQ || ownerId || visibility || unowned || tagIds.length > 0);

  function clearFilters() {
    setQ("");
    setDebouncedQ("");
    setOwnerId("");
    setVisibility("");
    setUnowned(false);
    setTagIds([]);
    setPage(0);
  }

  const columns: Column<Account>[] = [
    {
      header: "Name",
      width: "min-w-[16rem]",
      cell: (a) => <span>{a.name}</span>,
    },
    {
      header: "Visibility",
      width: "w-32",
      cell: (a) => <VisibilityBadge isPublic={a.is_public} />,
    },
    {
      header: "Owner",
      width: "w-40",
      hideOnSmall: true,
      cell: (a) => {
        if (!a.owner_id) return <span className="text-xs opacity-50">Unowned</span>;
        const u = usersById.get(a.owner_id);
        if (!u) return <span className="font-mono text-xs">{a.owner_id.slice(0, 8)}…</span>;
        return <span className="text-xs">{u.name || u.email.split("@")[0]}</span>;
      },
    },
    {
      header: "Created",
      width: "w-28",
      align: "right",
      hideOnSmall: true,
      cell: (a) => (
        <span className="text-xs opacity-60">{new Date(a.created_at).toLocaleDateString()}</span>
      ),
    },
  ];

  return (
    <div className="page-shell space-y-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Accounts</h1>
          <p className="text-sm opacity-60">
            Buyer / seller organizations. Visibility cascades from the account to its contacts and
            deals.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Link
              to="/accounts/trash"
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5"
              aria-label="Trash"
            >
              <Trash2 className="size-4" aria-hidden />
              <span className="hidden sm:inline">Trash</span>
            </Link>
          )}
          {isManagerPlus && (
            <ImportDialog
              entityLabel="Accounts"
              importPath="/api/accounts/import"
              templatePath="/api/accounts/import/template"
              columns={["name", "is_public", "address", "owner_email"]}
              onImported={() => queryClient.invalidateQueries({ queryKey: ["accounts"] })}
            />
          )}
          <Link
            to="/accounts/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            <Plus className="size-4" aria-hidden />
            <span className="hidden sm:inline">New account</span>
            <span className="sm:hidden">New</span>
          </Link>
        </div>
      </header>

      <section className="space-y-2 rounded-xl border border-[var(--border)] p-3">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 opacity-50"
            aria-hidden
          />
          <input
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder="Search account name…"
            className="w-full rounded-md border border-[var(--border)] bg-transparent py-2 pl-8 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          />
          {q && (
            <button
              type="button"
              onClick={() => {
                setQ("");
                setDebouncedQ("");
                setPage(0);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 opacity-50 hover:opacity-100"
              aria-label="Clear search"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <select
            value={visibility}
            onChange={(e) => {
              setVisibility(e.target.value as Visibility);
              setPage(0);
            }}
            className="rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            aria-label="Visibility"
          >
            <option value="">All visibility</option>
            <option value="public">Public</option>
            <option value="private">Private</option>
          </select>
          {isManagerPlus && (
            <SearchableUserPicker
              value={ownerId}
              onChange={(v) => {
                setOwnerId(v);
                setPage(0);
              }}
              users={usersQuery.data?.items ?? []}
              loading={usersQuery.isLoading}
              emptyLabel="All owners"
              ariaLabel="Filter by owner"
            />
          )}
          <label className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-2 py-1">
            <input
              type="checkbox"
              checked={unowned}
              onChange={(e) => {
                setUnowned(e.target.checked);
                setPage(0);
              }}
            />
            <span>Unowned only</span>
          </label>
          {hasFilter && (
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-md px-2 py-1 text-xs underline opacity-70 hover:opacity-100 sm:ml-auto"
            >
              Clear filters
            </button>
          )}
        </div>
        <TagFilterChips
          value={tagIds}
          onChange={(next) => {
            setTagIds(next);
            setPage(0);
          }}
        />
      </section>

      {listQuery.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : listQuery.error ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          Couldn't load accounts: {(listQuery.error as Error).message}
        </div>
      ) : (
        <>
          <div className="text-xs opacity-60">
            {data?.total ?? 0} {data?.total === 1 ? "account" : "accounts"}
            {hasFilter ? " match your filters" : ""}
          </div>
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(a) => a.id}
            rowHref={(a) => `/accounts/${a.id}`}
            empty={<EmptyState hasFilter={hasFilter} />}
          />
          <Pager
            page={page}
            totalPages={totalPages}
            total={data?.total ?? 0}
            shown={data?.items.length ?? 0}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          />
        </>
      )}
    </div>
  );
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  if (hasFilter) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--border)] p-8 text-center">
        <Search className="mx-auto mb-3 size-7 opacity-40" aria-hidden />
        <p className="text-sm font-medium">No matches</p>
        <p className="mt-1 text-sm opacity-60">Try a different search or clear the filters.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
      <Building2 className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
      <p className="text-sm font-medium">No accounts yet</p>
      <p className="mt-1 text-sm opacity-60">
        Create an account to group contacts and deals under it.
      </p>
      <Link
        to="/accounts/new"
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90"
      >
        <Plus className="size-4" aria-hidden />
        Create your first account
      </Link>
    </div>
  );
}

function VisibilityBadge({ isPublic }: { isPublic: boolean }) {
  if (isPublic) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
        <Globe className="size-3" aria-hidden />
        Public
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300">
      <Lock className="size-3" aria-hidden />
      Private
    </span>
  );
}

function Pager({
  page,
  totalPages,
  total,
  shown,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number;
  total: number;
  shown: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (total <= PAGE_SIZE) return null;
  return (
    <div className="flex items-center justify-between text-xs opacity-70">
      <span>
        Page {page + 1} of {totalPages} · {shown} shown
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={page === 0}
          className="rounded-md border border-[var(--border)] px-3 py-1 disabled:opacity-40"
        >
          Prev
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={page >= totalPages - 1}
          className="rounded-md border border-[var(--border)] px-3 py-1 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
